import { Button, MuiAlert, Skeleton, useTempDataContext } from '@fox/atoms';
import { FlexRowSpacedRightAligned } from '@fox/layout';
import { useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import { PaymentAddComponentProps } from '../../../contracts';
import { useEnsureScript } from '../../shared';

type PayWayFrame = {
	getToken: (
		tokenCallback: (
			err: undefined | { message: string },
			token: {
				singleUseTokenId: string;
				paymentMethod: 'creditCard';
				creditCard: {
					maskedCardNumber: string;
					expiryDateMonth: string;
					expiryDateYear: string;
					cardScheme: string;
					cardholderName: string;
					surchargePercentage: number;
					threeDS2AuthRequired: boolean;
				};
			}
		) => void
	) => void;
	destroy: () => void;
};
type PayWayCreatedCallback = (err: undefined | { message: string }, frame: PayWayFrame) => void;
type PayWayAPI = {
	createCreditCardFrame: (
		args: {
			publishableApiKey: string;
			container?: string;
			cvnRequired: boolean;
			style?: any;
			tokenMode: 'callback' | 'post';
			layout?: 'wide' | 'narrow';
			onValid: () => void;
			onInvalid: () => void;
		},
		createdCallback?: PayWayCreatedCallback
	) => PayWayFrame;
};

declare global {
	interface Window {
		payway: PayWayAPI;
	}
}

export function PayWayAddCreditCard({
	onSuccess,
	settings,
	completeIntent,
	intentData,
	onCancel,
	showSave
}: PaymentAddComponentProps<{ publishableKey: string }>) {
	const ready = useEnsureScript('https://api.payway.com.au/rest/v1/payway.js');
	const tempData = useTempDataContext();
	const [error, setError] = useState<string | undefined>(undefined);

	function saveToken() {
		return new Promise<number>((resolve, reject) => {
			if (typeof frameRef.current !== 'object') {
				setError('Card details is not ready to be saved');
				reject();
				return;
			}

			frameRef.current.getToken(async (err, token) => {
				if (err) {
					if (tempData) tempData.cardFormDataValid = false;
					setError(err.message);
					reject();
					return;
				}
				const result = await completeIntent(intentData?.intentId!, JSON.stringify(token));
				if (result) {
					if (tempData) tempData.cardFormDataValid = true;
					onSuccess(result);
					resolve(result);
					return;
				}
				if (tempData) tempData.cardFormDataValid = false;
				setError('Unable to save card information');
				reject();
			});
		});
	}

	// TODO - Clean this up so it's less hacky
	useImperativeHandle(
		r => {
			if (tempData) tempData.submitFunc = r;
		},
		() => ({
			submit: () => saveToken()
		})
	);
	const id = useId();
	const [valid, setValid] = useState(false);

	const frameRef = useRef<PayWayFrame | boolean>(false);

	useEffect(() => {
		let aborted = false;
		console.log('effect', ready, window.payway);
		if (!ready) return;

		// strict shenanegins
		if (frameRef.current === true) return;

		if (frameRef.current === false) {
			frameRef.current = true;

			const createdCallback: PayWayCreatedCallback = function (err, frame) {
				if (!err) {
					// TODO: Add event handler which calls frame.getToken
					console.log('callback frame', frame, aborted);
					if (aborted) return;
					frameRef.current = frame;
					return;
				}
				console.log('callback error', err);
			};

			window.payway.createCreditCardFrame(
				{
					container: id,
					publishableApiKey: settings?.publishableKey!,
					onValid: function () {
						console.log('valid');
						setValid(true);
					},
					onInvalid: function () {
						console.log('invalid');
						setValid(true);
					},
					tokenMode: 'callback',
					cvnRequired: false,
					layout: 'wide'
				},
				createdCallback
			);

			return () => {
				aborted = true;
				if (typeof frameRef.current === 'object') {
					frameRef.current.destroy();
					frameRef.current = false;
				}
			};
		}
	}, [ready]);

	if (!ready) return <Skeleton />;

	return (
		<>
			<div id={id} style={{ textAlign: 'center' }}></div>
			{error ? <MuiAlert severity="error">{error}</MuiAlert> : null}
			{showSave ? (
				<FlexRowSpacedRightAligned>
					<Button
						disabled={!valid}
						onClick={async () => {
							await saveToken();
						}}
					>
						Save
					</Button>
				</FlexRowSpacedRightAligned>
			) : null}
		</>
	);
}
